import React, { useState, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import toastr from "toastr";
import FlexDatesCard from "./FlexDatesCard";
import "../styles/Campaign.css";
import { CustomDropdown } from "./CustomDropdown";
import CustomToolTip from "./CustomToolTip";
import InfoBox from "./InfoBox";

interface EditCampaignModalProps {
    showEditCampaignModal: boolean;
    closeModal: () => void;
    refreshCampaigns: () => void;
    policy_id: string;
    SelectionDeadline: string;
    LaunchDate: string;
    ReminderDate: string;
    LastDayReminder: string;
    benefitEndDate: string;
    hasAccrualEnabled: boolean;
    nextYearAccrualFrequency: string | null;
    IsPlanAdmin: boolean;
    setParentLoading: (loading: boolean) => void;
    onFailedSave: () => void;
}

const EditCampaignModal: React.FC<EditCampaignModalProps> = ({
    showEditCampaignModal,
    closeModal,
    refreshCampaigns,
    policy_id,
    SelectionDeadline: initialSelectionDeadline,
    LaunchDate: initialLaunchDate,
    ReminderDate: initialReminderDate,
    LastDayReminder: initialLastDayReminder,
    benefitEndDate,
    hasAccrualEnabled,
    nextYearAccrualFrequency,
    IsPlanAdmin,
    setParentLoading,
    onFailedSave
}) => {
    const [SelectionDeadline, setSelectionDeadline] = useState(initialSelectionDeadline);
    const [LaunchDate, setLaunchDate] = useState(initialLaunchDate);
    const [ReminderDate, setReminderDate] = useState(initialReminderDate);
    const [LastDayReminder, setLastDayReminder] = useState(initialLastDayReminder);
    const [nextYearScheduledCreditsFrequency, setnextYearScheduledCreditsFrequency] = useState<string | null>(null);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    useEffect(() => {
        setnextYearScheduledCreditsFrequency(nextYearAccrualFrequency || "None");
    }, [nextYearAccrualFrequency]);

    const handleSave = async () => {
        let selectionDeadline = new Date(SelectionDeadline);
        let launchDate = new Date(LaunchDate);
        let reminderDate = new Date(ReminderDate);
        let lastDayReminder = new Date(LastDayReminder);
        let benefitEndDateObj = new Date(benefitEndDate);
        let originalLaunchDate = new Date(initialLaunchDate);

        if (launchDate.getTime() !== originalLaunchDate.getTime() && launchDate <= today) {
            toastr.error("Launch date must be after today", "Error");
            return;
        }

        if (
            selectionDeadline > benefitEndDateObj ||
            launchDate > benefitEndDateObj ||
            reminderDate > benefitEndDateObj ||
            lastDayReminder > benefitEndDateObj
        ) {
            toastr.error("Dates cannot be after the benefit year end date", "Error");
            return;
        }

        if (
            launchDate >= selectionDeadline ||
            reminderDate >= selectionDeadline ||
            lastDayReminder >= selectionDeadline
        ) {
            toastr.error("Dates must be before the selection deadline", "Error");
            return;
        }

        if (launchDate >= reminderDate) {
            toastr.error("Launch date must be before the reminder date", "Error");
            return;
        }

        if (reminderDate >= lastDayReminder) {
            toastr.error("Reminder date must be before the last day reminder", "Error");
            return;
        }

        const hasLaunchDatePassed = launchDate < today;

        const body = {
            PolicyId: policy_id,
            SelectionDeadline: selectionDeadline.toLocaleDateString(),
            LaunchDate: launchDate.toLocaleDateString(),
            ReminderDate: reminderDate.toLocaleDateString(),
            LastDayReminder: lastDayReminder.toLocaleDateString(),
            NextYearScheduledCreditsFrequency:
                nextYearScheduledCreditsFrequency === "None" ? null : nextYearScheduledCreditsFrequency,
            HasLaunchDatePassed: hasLaunchDatePassed,
        };

        setParentLoading(true);
        closeModal();

        try {
            const response = await axios.put("/api/Flex/editpolicycampaign", body);
            if (response.data.success) {
                refreshCampaigns();
                toastr.success("Campaign updated successfully");
            } else {
                toastr.error(response.data.message || "Failed to update campaign");
                onFailedSave();
            }
        } catch (error) {
            toastr.error("An error occurred while updating the campaign");
            onFailedSave();
        } finally {
            setParentLoading(false);
        }
    };

    const handleDateChange = (date: string, type: string) => {
        switch (type) {
            case "SelectionDeadline":
                setSelectionDeadline(date);
                break;
            case "LaunchDate":
                setLaunchDate(date);
                break;
            case "ReminderDate":
                setReminderDate(date);
                break;
            case "LastDayReminder":
                setLastDayReminder(date);
                break;
            default:
                break;
        }
    };

    const isAnyDateNull = !SelectionDeadline || !LaunchDate || !ReminderDate || !LastDayReminder;

    const isEditable = (date: string) => {
        return new Date(date) > today;
    };

    const hasLaunchDatePassed = new Date(LaunchDate) < today;

    return (
        <Modal
            className="react-modal edit-campaign-modal"
            show={showEditCampaignModal}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header>
                <Modal.Title className="modal-title">Edit Campaign Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FlexDatesCard
                    handleDateChange={handleDateChange}
                    LaunchDate={LaunchDate}
                    ReminderDate={ReminderDate}
                    LastDayReminder={LastDayReminder}
                    SelectionDeadline={SelectionDeadline}
                    isEditable={isEditable}
                />

                {(hasAccrualEnabled && !IsPlanAdmin) && (
                    <div
                        className="RT-TextBox"
                        style={{
                            backgroundColor: "white",
                            padding: "15px 20px",
                            marginTop: "20px",
                            borderRadius: "8px",
                        }}
                    >
                       
                        <label className="RT-Label" style={{ paddingLeft: 10 }}>
                            Scheduled Credits Frequency
                            <CustomToolTip
                                header="Scheduled Credits Frequency"
                                bodyContent="Confirm the Frequency for next year's Scheduled Credits. Only Ops users may make this adjustment."
                            />
                        </label>

                        <CustomDropdown
                            options={["None", "Monthly", "Quarterly", "Semiannually"]}
                            selectedOption={nextYearScheduledCreditsFrequency}
                            setSelectedOption={setnextYearScheduledCreditsFrequency}
                        />

                        {hasLaunchDatePassed && (
                            <InfoBox style={{marginTop: 10}}>
                                The launch date has already passed. Editing this frequency will trigger
                                another Flex Allocation email to all Flex employees with Scheduled Credits enabled.
                            </InfoBox>
                        )}

                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isAnyDateNull}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditCampaignModal;
